import { createSkinDocument } from "./document";
import {
  RGBA_CHANNELS,
  MAX_SKIN_SIZE,
  SKIN_SIZE,
  SKIN_FACES,
  type SkinDocument,
  type SkinFace,
  type SkinModel,
  type SkinPart,
  type SkinSize,
  type SurfaceRef,
} from "./types";
import { getSurfaceRect, getSurfaceSize, surfaceToUv } from "./uv";

const PNG_SIGNATURE = new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10]);
const textEncoder = new TextEncoder();

export class SkinPngError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SkinPngError";
  }
}

/** Encodes the exact 64x64 or 128x128 RGBA document without resampling. */
export async function exportSkinPng(document: SkinDocument): Promise<Blob> {
  const size = document.size;
  if (document.pixels.length !== size * size * RGBA_CHANNELS) {
    throw new SkinPngError(`皮肤文档不是有效的 ${size}×${size} RGBA 像素。`);
  }
  const scanlines = new Uint8Array(size * (1 + size * RGBA_CHANNELS));
  const rowBytes = size * RGBA_CHANNELS;
  for (let y = 0; y < size; y += 1) {
    const destination = y * (rowBytes + 1);
    scanlines[destination] = 0;
    scanlines.set(
      document.pixels.subarray(y * rowBytes, (y + 1) * rowBytes),
      destination + 1,
    );
  }

  const compressed = await transformBytes(
    scanlines,
    new CompressionStream("deflate"),
  );
  const ihdr = new Uint8Array(13);
  const view = new DataView(ihdr.buffer);
  view.setUint32(0, size);
  view.setUint32(4, size);
  ihdr[8] = 8; // bit depth
  ihdr[9] = 6; // RGBA
  ihdr[10] = 0; // compression
  ihdr[11] = 0; // filter
  ihdr[12] = 0; // no interlace

  const png = concatBytes(
    PNG_SIGNATURE,
    createChunk("IHDR", ihdr),
    createChunk("IDAT", compressed),
    createChunk("IEND"),
  );
  return new Blob([toArrayBuffer(png)], { type: "image/png" });
}

/** Decodes Java legacy, Java modern, or Bedrock HD humanoid skin PNGs. */
export async function importSkinPng(
  blob: Blob,
  model?: SkinModel,
): Promise<SkinDocument> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  assertSignature(bytes);

  let cursor = PNG_SIGNATURE.length;
  let width = 0;
  let height = 0;
  let bitDepth = 0;
  let colorType = -1;
  let interlace = -1;
  const idatChunks: Uint8Array[] = [];
  let palette: Uint8Array | undefined;
  let transparency: Uint8Array | undefined;

  while (cursor + 12 <= bytes.length) {
    const length = readUint32(bytes, cursor);
    const typeStart = cursor + 4;
    const dataStart = cursor + 8;
    const dataEnd = dataStart + length;
    if (dataEnd + 4 > bytes.length)
      throw new SkinPngError("PNG 数据块被截断。");
    const type = String.fromCharCode(
      ...bytes.subarray(typeStart, typeStart + 4),
    );
    const data = bytes.subarray(dataStart, dataEnd);
    const expectedCrc = readUint32(bytes, dataEnd);
    const actualCrc = crc32(bytes.subarray(typeStart, dataEnd));
    if (actualCrc !== expectedCrc)
      throw new SkinPngError(`PNG ${type} 数据块校验失败。`);

    if (type === "IHDR") {
      if (data.length !== 13) throw new SkinPngError("PNG IHDR 长度无效。");
      width = readUint32(data, 0);
      height = readUint32(data, 4);
      bitDepth = data[8];
      colorType = data[9];
      if (data[10] !== 0 || data[11] !== 0)
        throw new SkinPngError("PNG 使用了不支持的压缩或滤波方式。");
      interlace = data[12];
    } else if (type === "IDAT") {
      idatChunks.push(new Uint8Array(data));
    } else if (type === "PLTE") {
      palette = new Uint8Array(data);
    } else if (type === "tRNS") {
      transparency = new Uint8Array(data);
    } else if (type === "IEND") {
      break;
    }
    cursor = dataEnd + 4;
  }

  const isLegacy = width === SKIN_SIZE && height === 32;
  const isModern =
    width === height && (width === SKIN_SIZE || width === MAX_SKIN_SIZE);
  if (!isLegacy && !isModern) {
    throw new SkinPngError(
      `仅支持 64×32、64×64 或 128×128 人形皮肤，收到：${width}×${height}`,
    );
  }
  if (bitDepth !== 8)
    throw new SkinPngError(`仅支持 8 位 PNG，收到位深：${bitDepth}`);
  if (interlace !== 0 && interlace !== 1)
    throw new SkinPngError(`PNG 使用了未知交错方式：${interlace}`);
  if (![0, 2, 3, 4, 6].includes(colorType))
    throw new SkinPngError(`不支持 PNG 颜色类型：${colorType}`);
  if (idatChunks.length === 0) throw new SkinPngError("PNG 不包含像素数据。");
  if (colorType === 3 && (!palette || palette.length % 3 !== 0)) {
    throw new SkinPngError("索引色 PNG 缺少有效调色板。");
  }

  const channels = channelsForColorType(colorType);
  const expectedLength = packedScanlineLength(
    width,
    height,
    channels,
    interlace,
  );
  const packed = await transformBytes(
    concatBytes(...idatChunks),
    new DecompressionStream("deflate"),
    expectedLength,
  );
  if (packed.length !== expectedLength) {
    throw new SkinPngError(
      `PNG 解压后的像素长度无效：应为 ${expectedLength}，收到 ${packed.length}`,
    );
  }
  const raw =
    interlace === 1
      ? deinterlaceAdam7(packed, width, height, channels)
      : unfilter(packed, width, height, channels);
  const decoded = convertToRgba(
    raw,
    width * height,
    colorType,
    palette,
    transparency,
  );
  const pixels = isLegacy ? upgradeLegacySkin(decoded) : decoded;
  const inferredModel = isLegacy
    ? "classic"
    : inferSkinModel(pixels, width as SkinSize);
  return createSkinDocument(pixels, model ?? inferredModel);
}

export const encodeSkinPng = exportSkinPng;
export const decodeSkinPng = importSkinPng;

export function inferSkinModel(
  pixels: Uint8ClampedArray,
  size: SkinSize = pixels.length ===
  MAX_SKIN_SIZE * MAX_SKIN_SIZE * RGBA_CHANNELS
    ? MAX_SKIN_SIZE
    : SKIN_SIZE,
): SkinModel {
  const scale = size / SKIN_SIZE;
  const unusedSlimAreas = [
    { x: 50, y: 16, width: 2, height: 4 },
    { x: 54, y: 20, width: 2, height: 12 },
    { x: 42, y: 48, width: 2, height: 4 },
    { x: 46, y: 52, width: 2, height: 12 },
  ].map((area) => ({
    x: area.x * scale,
    y: area.y * scale,
    width: area.width * scale,
    height: area.height * scale,
  }));
  const everyAreaIs = (
    red: number,
    green: number,
    blue: number,
    alpha: number,
  ): boolean =>
    unusedSlimAreas.every((area) =>
      everyPixel(
        area.x,
        area.y,
        area.width,
        area.height,
        (offset) =>
          pixels[offset] === red &&
          pixels[offset + 1] === green &&
          pixels[offset + 2] === blue &&
          pixels[offset + 3] === alpha,
      ),
    );
  const hasTransparentMarker = unusedSlimAreas.some((area) =>
    somePixel(
      area.x,
      area.y,
      area.width,
      area.height,
      (offset) => pixels[offset + 3] === 0,
    ),
  );
  return hasTransparentMarker ||
    everyAreaIs(0, 0, 0, 255) ||
    everyAreaIs(255, 255, 255, 255)
    ? "slim"
    : "classic";

  function everyPixel(
    x: number,
    y: number,
    width: number,
    height: number,
    predicate: (offset: number) => boolean,
  ): boolean {
    for (let row = y; row < y + height; row += 1) {
      for (let column = x; column < x + width; column += 1) {
        if (!predicate((row * size + column) * RGBA_CHANNELS)) return false;
      }
    }
    return true;
  }

  function somePixel(
    x: number,
    y: number,
    width: number,
    height: number,
    predicate: (offset: number) => boolean,
  ): boolean {
    for (let row = y; row < y + height; row += 1) {
      for (let column = x; column < x + width; column += 1) {
        if (predicate((row * size + column) * RGBA_CHANNELS)) return true;
      }
    }
    return false;
  }
}

function assertSignature(bytes: Uint8Array): void {
  if (
    bytes.length < PNG_SIGNATURE.length ||
    !PNG_SIGNATURE.every((byte, index) => bytes[index] === byte)
  ) {
    throw new SkinPngError("文件不是有效的 PNG。");
  }
}

function channelsForColorType(colorType: number): number {
  switch (colorType) {
    case 0:
    case 3:
      return 1;
    case 2:
      return 3;
    case 4:
      return 2;
    case 6:
      return 4;
    default:
      throw new SkinPngError(`不支持 PNG 颜色类型：${colorType}`);
  }
}

function unfilter(
  scanlines: Uint8Array,
  width: number,
  height: number,
  channels: number,
): Uint8Array {
  const rowBytes = width * channels;
  const output = new Uint8Array(rowBytes * height);
  for (let y = 0; y < height; y += 1) {
    const scanlineOffset = y * (rowBytes + 1);
    const filter = scanlines[scanlineOffset];
    if (filter > 4) throw new SkinPngError(`PNG 使用了未知滤波器：${filter}`);
    const outputOffset = y * rowBytes;
    for (let x = 0; x < rowBytes; x += 1) {
      const value = scanlines[scanlineOffset + 1 + x];
      const left = x >= channels ? output[outputOffset + x - channels] : 0;
      const up = y > 0 ? output[outputOffset - rowBytes + x] : 0;
      const upLeft =
        y > 0 && x >= channels
          ? output[outputOffset - rowBytes + x - channels]
          : 0;
      let predictor = 0;
      if (filter === 1) predictor = left;
      else if (filter === 2) predictor = up;
      else if (filter === 3) predictor = Math.floor((left + up) / 2);
      else if (filter === 4) predictor = paeth(left, up, upLeft);
      output[outputOffset + x] = (value + predictor) & 0xff;
    }
  }
  return output;
}

const ADAM7_PASSES = [
  { x: 0, y: 0, stepX: 8, stepY: 8 },
  { x: 4, y: 0, stepX: 8, stepY: 8 },
  { x: 0, y: 4, stepX: 4, stepY: 8 },
  { x: 2, y: 0, stepX: 4, stepY: 4 },
  { x: 0, y: 2, stepX: 2, stepY: 4 },
  { x: 1, y: 0, stepX: 2, stepY: 2 },
  { x: 0, y: 1, stepX: 1, stepY: 2 },
] as const;

function packedScanlineLength(
  width: number,
  height: number,
  channels: number,
  interlace: number,
): number {
  if (interlace === 0) return height * (width * channels + 1);
  return ADAM7_PASSES.reduce((total, pass) => {
    const passWidth = passSize(width, pass.x, pass.stepX);
    const passHeight = passSize(height, pass.y, pass.stepY);
    return passWidth === 0 || passHeight === 0
      ? total
      : total + passHeight * (passWidth * channels + 1);
  }, 0);
}

function deinterlaceAdam7(
  packed: Uint8Array,
  width: number,
  height: number,
  channels: number,
): Uint8Array {
  const output = new Uint8Array(width * height * channels);
  let packedOffset = 0;

  for (const pass of ADAM7_PASSES) {
    const passWidth = passSize(width, pass.x, pass.stepX);
    const passHeight = passSize(height, pass.y, pass.stepY);
    if (passWidth === 0 || passHeight === 0) continue;
    const passLength = passHeight * (passWidth * channels + 1);
    const rawPass = unfilter(
      packed.subarray(packedOffset, packedOffset + passLength),
      passWidth,
      passHeight,
      channels,
    );
    packedOffset += passLength;

    for (let passY = 0; passY < passHeight; passY += 1) {
      for (let passX = 0; passX < passWidth; passX += 1) {
        const source = (passY * passWidth + passX) * channels;
        const x = pass.x + passX * pass.stepX;
        const y = pass.y + passY * pass.stepY;
        output.set(
          rawPass.subarray(source, source + channels),
          (y * width + x) * channels,
        );
      }
    }
  }
  return output;
}

function passSize(total: number, start: number, step: number): number {
  return total <= start ? 0 : Math.ceil((total - start) / step);
}

function convertToRgba(
  raw: Uint8Array,
  pixelCount: number,
  colorType: number,
  palette?: Uint8Array,
  transparency?: Uint8Array,
): Uint8ClampedArray {
  const channels = channelsForColorType(colorType);
  const output = new Uint8ClampedArray(pixelCount * RGBA_CHANNELS);
  for (let pixel = 0; pixel < pixelCount; pixel += 1) {
    const source = pixel * channels;
    const destination = pixel * RGBA_CHANNELS;
    if (colorType === 6) {
      output.set(raw.subarray(source, source + 4), destination);
    } else if (colorType === 2) {
      output[destination] = raw[source];
      output[destination + 1] = raw[source + 1];
      output[destination + 2] = raw[source + 2];
      output[destination + 3] =
        transparency &&
        transparency.length >= 6 &&
        raw[source] === readUint16(transparency, 0) &&
        raw[source + 1] === readUint16(transparency, 2) &&
        raw[source + 2] === readUint16(transparency, 4)
          ? 0
          : 255;
    } else if (colorType === 4) {
      output[destination] = raw[source];
      output[destination + 1] = raw[source];
      output[destination + 2] = raw[source];
      output[destination + 3] = raw[source + 1];
    } else if (colorType === 0) {
      output[destination] = raw[source];
      output[destination + 1] = raw[source];
      output[destination + 2] = raw[source];
      output[destination + 3] =
        transparency &&
        transparency.length >= 2 &&
        raw[source] === readUint16(transparency, 0)
          ? 0
          : 255;
    } else {
      const index = raw[source];
      const paletteOffset = index * 3;
      if (!palette || paletteOffset + 2 >= palette.length)
        throw new SkinPngError("PNG 调色板索引越界。");
      output[destination] = palette[paletteOffset];
      output[destination + 1] = palette[paletteOffset + 1];
      output[destination + 2] = palette[paletteOffset + 2];
      output[destination + 3] = transparency?.[index] ?? 255;
    }
  }
  return output;
}

function upgradeLegacySkin(legacyPixels: Uint8ClampedArray): Uint8ClampedArray {
  const pixels = new Uint8ClampedArray(SKIN_SIZE * SKIN_SIZE * RGBA_CHANNELS);
  pixels.set(legacyPixels);
  if (!hasTransparentPixel(legacyPixels)) clearLegacyHatOverlay(pixels);
  copyMirroredLegacyPart(pixels, "rightArm", "leftArm");
  copyMirroredLegacyPart(pixels, "rightLeg", "leftLeg");
  return pixels;
}

function hasTransparentPixel(pixels: Uint8ClampedArray): boolean {
  for (let offset = 3; offset < pixels.length; offset += RGBA_CHANNELS) {
    if (pixels[offset] !== 255) return true;
  }
  return false;
}

function clearLegacyHatOverlay(pixels: Uint8ClampedArray): void {
  for (const face of SKIN_FACES) {
    const rect = getSurfaceRect(
      { part: "head", layer: "outer", face },
      "classic",
    );
    for (let y = rect.y; y < rect.y + rect.height; y += 1) {
      for (let x = rect.x; x < rect.x + rect.width; x += 1) {
        const offset = (y * SKIN_SIZE + x) * RGBA_CHANNELS;
        pixels.fill(0, offset, offset + RGBA_CHANNELS);
      }
    }
  }
}

function copyMirroredLegacyPart(
  pixels: Uint8ClampedArray,
  fromPart: SkinPart,
  toPart: SkinPart,
): void {
  const snapshot = new Uint8ClampedArray(pixels);
  for (const sourceFace of SKIN_FACES) {
    const targetFace = mirrorFace(sourceFace);
    const from = {
      part: fromPart,
      layer: "inner",
      face: sourceFace,
    } satisfies SurfaceRef;
    const to = {
      part: toPart,
      layer: "inner",
      face: targetFace,
    } satisfies SurfaceRef;
    const surfaceSize = getSurfaceSize(from, "classic");
    for (let y = 0; y < surfaceSize.height; y += 1) {
      for (let x = 0; x < surfaceSize.width; x += 1) {
        const source = surfaceToUv(
          from,
          surfaceSize.width - 1 - x,
          y,
          "classic",
        );
        const target = surfaceToUv(to, x, y, "classic");
        const sourceOffset = (source.y * SKIN_SIZE + source.x) * RGBA_CHANNELS;
        const targetOffset = (target.y * SKIN_SIZE + target.x) * RGBA_CHANNELS;
        pixels.set(
          snapshot.subarray(sourceOffset, sourceOffset + RGBA_CHANNELS),
          targetOffset,
        );
      }
    }
  }
}

function mirrorFace(face: SkinFace): SkinFace {
  if (face === "left") return "right";
  if (face === "right") return "left";
  return face;
}

function paeth(left: number, up: number, upLeft: number): number {
  const prediction = left + up - upLeft;
  const leftDistance = Math.abs(prediction - left);
  const upDistance = Math.abs(prediction - up);
  const upLeftDistance = Math.abs(prediction - upLeft);
  return leftDistance <= upDistance && leftDistance <= upLeftDistance
    ? left
    : upDistance <= upLeftDistance
      ? up
      : upLeft;
}

function createChunk(
  type: string,
  data: Uint8Array<ArrayBufferLike> = new Uint8Array(),
): Uint8Array {
  const typeBytes = textEncoder.encode(type);
  const chunk = new Uint8Array(12 + data.length);
  new DataView(chunk.buffer).setUint32(0, data.length);
  chunk.set(typeBytes, 4);
  chunk.set(data, 8);
  new DataView(chunk.buffer).setUint32(
    8 + data.length,
    crc32(chunk.subarray(4, 8 + data.length)),
  );
  return chunk;
}

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc ^= byte;
    for (let bit = 0; bit < 8; bit += 1)
      crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1));
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function readUint32(bytes: Uint8Array, offset: number): number {
  return new DataView(bytes.buffer, bytes.byteOffset + offset, 4).getUint32(0);
}

function readUint16(bytes: Uint8Array, offset: number): number {
  return new DataView(bytes.buffer, bytes.byteOffset + offset, 2).getUint16(0);
}

function concatBytes(
  ...arrays: readonly Uint8Array<ArrayBufferLike>[]
): Uint8Array {
  const result = new Uint8Array(
    arrays.reduce((total, array) => total + array.length, 0),
  );
  let offset = 0;
  for (const array of arrays) {
    result.set(array, offset);
    offset += array.length;
  }
  return result;
}

async function transformBytes(
  bytes: Uint8Array,
  transform: CompressionStream | DecompressionStream,
  maximumOutputBytes = Number.POSITIVE_INFINITY,
): Promise<Uint8Array> {
  const outputPromise = readStream(transform.readable, maximumOutputBytes);
  const writer = transform.writable.getWriter();
  const writePromise = (async () => {
    await writer.write(new Uint8Array(toArrayBuffer(bytes)));
    await writer.close();
  })();
  try {
    const [output] = await Promise.all([outputPromise, writePromise]);
    return output;
  } catch (error) {
    void writer.abort(error).catch(() => undefined);
    throw error;
  }
}

async function readStream(
  stream: ReadableStream<Uint8Array>,
  maximumBytes = Number.POSITIVE_INFINITY,
): Promise<Uint8Array> {
  const reader = stream.getReader();
  const chunks: Uint8Array[] = [];
  let totalBytes = 0;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    totalBytes += value.byteLength;
    if (totalBytes > maximumBytes) {
      await reader.cancel("PNG 解压数据超出支持的皮肤像素上限。");
      throw new SkinPngError("PNG 解压数据超出支持的皮肤像素上限。");
    }
    chunks.push(new Uint8Array(value));
  }
  return concatBytes(...chunks);
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  ) as ArrayBuffer;
}
